import { apiClient } from '@/shared/api/http-client';
import { createApiPath } from '@/shared/api/endpoints';
import { Pageable, PageResponse } from '@/entities/user/api/user.api';

export interface BusinessRelationshipRequest {
  clinicName?: string;
  pharmaCompanyName?: string;
  email?: string;
  ceoName?: string;
  /** 사용자 업체명 검색 — 서버가 양쪽 부분 일치·대소문자 무시로 찾는다 */
  companyName?: string;
  transactionStatus?: string;
  drugApprovalStatus?: string;
  parentCompanyId?: number;
  settlementCompanyId?: number;
}

export interface TransactionStatusHistoryItem {
  status: string;
  changedAt: string;
}

export interface BusinessRelationshipResponse {
  relationPharmaCompanyId: number;
  clinicId?: number;
  email: string;
  /** 대표자명 */
  ceoName: string;
  /** 사용자 업체명(user_profile.company_name). 미입력 사용자는 null */
  companyName?: string | null;
  clinicName: string;
  clinicTypeCode: string;
  clinicType?: 'HOSPITAL' | 'CLINIC';
  /** 병의원 폐업일(yyyy-MM-dd). null이면 영업 중 */
  clinicClosedOn?: string | null;
  /** 연계 문전약국. 상세와 같은 객체 배열 */
  pharmacies?: RelatedPharmacy[];
  pharmaCompanyName: string;
  drugCode?: string | null;
  drugName?: string | null;
  transactionStatus: string;
  drugApprovalStatus?: string | null;
  applicationDate: string;
  approvalDate: string;
  drugApplicationDate?: string | null;
  drugApprovalDate?: string | null;
  parentCompanyId?: number | null;
  parentCompanyName?: string | null;
  settlementCompanyId?: number | null;
  settlementCompanyName?: string | null;
  /** 
   * 레거시 매핑을 위한 필드들.
   * pharmaCompanyId는 relationPharmaCompanyId와 별개 필드임 (요구사항 명시).
   */
  pharmaCompanyId?: number;
  drugId?: number;
}

export interface RelatedPharmacy {
  pharmacyName: string;
  /** 폐업일(yyyy-MM-dd). null/undefined면 영업 중 */
  pharmacyClosedOn?: string | null;
}

export interface BusinessRelationshipDetailResponse {
  relationPharmaCompanyId: number;
  transactionStatus: string;
  applicationDate: string;
  approvalDate: string;
  resubmissionDate: string;
  availableDrugCodes: string[];
  unavailableDrugCodes: string[];
  relationshipId: number;
  targetDepartment: string;
  kolName: string;
  ediReceivable: boolean;
  drugDc: boolean;
  drugDcFrom: string;
  drugDcTo: string;
  codingAvailableMonth: string;
  clinicVerificationImage: string;
  clinicId: number;
  clinicName: string;
  clinicTypeCode: string;
  businessStatus: string;
  businessLicenseImage: string;
  email: string;
  companyName: string;
  phoneNumber: string;
  pharmaCompanyId: number;
  pharmaCompanyName: string;
  parentCompanyId?: number | null;
  parentCompanyName?: string | null;
  settlementCompanyId?: number | null;
  settlementCompanyName?: string | null;
  partnerCompanyId?: number | null;
  partnerCompanyName?: string | null;
  /** 병의원 폐업일(yyyy-MM-dd). null이면 영업 중 */
  clinicClosedOn?: string | null;
  pharmacies: RelatedPharmacy[];
  totalMonthlyExpectedPrescriptionAmount: number;
  drugName: string;
  drugCode?: string | null;
  drugApprovalStatus?: string | null;
  drugApplicationDate?: string | null;
  drugApprovalDate?: string | null;
  transactionStatusHistories?: TransactionStatusHistoryItem[];
  clinicType: 'HOSPITAL' | 'CLINIC';
  departments: string[];
}

export interface BusinessRelationshipUpdateRequest {
  totalMonthlyExpectedPrescriptionAmount?: number | null;
  availableDrugCodes?: string[];
  unavailableDrugCodes?: string[];
  transactionStatus?:
    | '신청'
    | '확인 중'
    | '이관 확인 중'
    | '승인'
    | '거절'
    | '재신청'
    | '종료';
  approvalDate?: string | null;
  drugCode?: string | null;
  drugApprovalStatus?: string;
  drugApprovalDate?: string | null;
}

/**
 * PUT …/companies 요청 body.
 * drugCode는 병원급 의약품 단위 지정 시에만 포함한다 (의원급에 보내면 400).
 */
export interface BusinessRelationshipCompaniesUpdateRequest {
  parentCompanyId: number;
  settlementCompanyId: number;
  drugCode?: string;
}

/**
 * GET /admin/business-relationships/export-all 한 행.
 * 백엔드 BusinessRelationshipCsvResponse 와 필드·순서를 맞춘다.
 */
export interface BusinessRelationshipCsvResponse {
  relationPharmaCompanyId: number;
  clinicId: number;
  userId: number;
  email: string;
  ceoName: string;
  /** 사용자 업체명. 미입력 사용자는 null */
  companyName?: string | null;
  clinicName: string;
  /** 원천 코드 (예: "03") */
  clinicTypeCode: string;
  /** 한글 유형 (예: "의원급") */
  clinicType?: string;
  /** 병의원 폐업일(yyyy-MM-dd) */
  clinicClosedOn?: string | null;
  /**
   * 연계 약국 한 셀 문자열.
   * 예: `루다약국, 폐업약국(폐업 2026-09-10)` — 목록·상세의 객체 배열과 다름.
   */
  pharmacies?: string;
  pharmaCompanyName: string;
  transactionStatus: string;
  applicationDate: string;
  approvalDate: string;
  drugCode?: string | null;
  drugName?: string | null;
  drugApplicationDate?: string | null;
  drugApprovalDate?: string | null;
  /** 한글 표시값 (예: "승인") */
  drugApprovalStatus?: string | null;
}

export interface BusinessRelationshipUploadItem {
  userId: number;
  email?: string;
  clinicId: number;
  clinicName?: string;
  parentCompanyId: number;
  settlementCompanyId: number;
  pharmaCompanyName: string;
  drugCode: string;
  drugName?: string;
  applicationDate: string;
  approvalDate: string;
  /** 미전송·빈 값이면 서버가 APPROVED(승인)로 등록 */
  transactionStatus?: string;
  drugApplicationDate: string;
  drugApprovalDate: string;
  totalMonthlyExpectedPrescriptionAmount: number;
}

/** PATCH …/transaction-status 대상 행. 목록에서 체크한 줄을 그대로 보낸다 */
export interface BusinessRelationshipBulkStatusTarget {
  relationPharmaCompanyId: number;
  /** 없으면 null. 있으면 해당 의약품 drugApprovalStatus도 함께 변경 */
  drugCode?: string | null;
}

export interface BusinessRelationshipBulkStatusUpdateRequest {
  transactionStatus: string;
  targets: BusinessRelationshipBulkStatusTarget[];
}

export interface BusinessRelationshipBulkStatusFailedItem {
  relationPharmaCompanyId: number;
  drugCode?: string | null;
  reasonCode: string;
  reason: string;
}

export interface BusinessRelationshipBulkStatusSucceededItem {
  relationPharmaCompanyId: number;
  drugCode?: string | null;
}

/**
 * 일괄 상태 변경 응답.
 * successCount는 요청 행 수, statusChangedCount는 실제로 상태가 바뀐 거래처 수.
 */
export interface BusinessRelationshipBulkStatusUpdateResponse {
  requestedCount: number;
  successCount: number;
  failedCount: number;
  statusChangedCount: number;
  succeeded: BusinessRelationshipBulkStatusSucceededItem[];
  failed: BusinessRelationshipBulkStatusFailedItem[];
}

/** 일괄 변경 targets 상한 — 서버 BUSINESS_RELATIONSHIP_BULK_SIZE_EXCEEDED */
export const BUSINESS_BULK_STATUS_MAX_TARGETS = 1000;

export const businessService = {
  getBusinessRelationships: async (
    searchRequest: BusinessRelationshipRequest,
    pageable: Pageable,
  ): Promise<PageResponse<BusinessRelationshipResponse>> => {
    const params: Record<string, string | number> = {
      page: pageable.page,
      size: pageable.size,
    };

    if (searchRequest.clinicName) params.clinicName = searchRequest.clinicName;
    if (searchRequest.pharmaCompanyName) {
      params.pharmaCompanyName = searchRequest.pharmaCompanyName;
    }
    if (searchRequest.email) params.email = searchRequest.email;
    if (searchRequest.ceoName) params.ceoName = searchRequest.ceoName;
    if (searchRequest.companyName) {
      params.companyName = searchRequest.companyName;
    }
    if (searchRequest.transactionStatus) {
      params.transactionStatus = searchRequest.transactionStatus;
    }
    if (searchRequest.drugApprovalStatus) {
      params.drugApprovalStatus = searchRequest.drugApprovalStatus;
    }
    if (searchRequest.parentCompanyId != null) {
      params.parentCompanyId = searchRequest.parentCompanyId;
    }
    if (searchRequest.settlementCompanyId != null) {
      params.settlementCompanyId = searchRequest.settlementCompanyId;
    }

    const response = await apiClient.get(createApiPath.business.list(), {
      params,
    });
    return response.data;
  },

  getBusinessRelationshipDetail: async (
    relationPharmaCompanyId: number,
    drugCode?: string,
  ): Promise<BusinessRelationshipDetailResponse> => {
    const response = await apiClient.get(
      createApiPath.business.detail(relationPharmaCompanyId),
      drugCode ? { params: { drugCode } } : undefined,
    );
    return response.data;
  },

  updateBusinessRelationship: async (
    relationPharmaCompanyId: number,
    updateData: BusinessRelationshipUpdateRequest,
  ): Promise<void> => {
    await apiClient.put(
      createApiPath.business.update(relationPharmaCompanyId),
      updateData,
    );
  },

  /**
   * 선택 행 거래 상태 일괄 변경.
   * 존재하지 않는 대상이 섞여도 부분 성공 응답을 돌려준다.
   */
  updateBusinessRelationshipTransactionStatuses: async (
    request: BusinessRelationshipBulkStatusUpdateRequest,
  ): Promise<BusinessRelationshipBulkStatusUpdateResponse> => {
    const response =
      await apiClient.patch<BusinessRelationshipBulkStatusUpdateResponse>(
        createApiPath.business.bulkTransactionStatus(),
        request,
      );
    return response.data;
  },

  /**
   * 상위업체·정산업체 지정.
   * 의원급은 제약사 단위, 병원급은 drugCode가 있으면 의약품 단위(없으면 제약사 전체).
   */
  updateBusinessRelationshipCompanies: async (
    relationPharmaCompanyId: number,
    request: BusinessRelationshipCompaniesUpdateRequest,
  ): Promise<void> => {
    await apiClient.put(
      createApiPath.business.companies(relationPharmaCompanyId),
      request,
    );
  },

  uploadBusinessRelationshipsFromExcel: async (
    items: BusinessRelationshipUploadItem[],
  ): Promise<void> => {
    await apiClient.post(createApiPath.business.excelUpload(), { items });
  },

  exportAllBusinessRelationships: async (): Promise<
    BusinessRelationshipCsvResponse[]
  > => {
    const response = await apiClient.get(createApiPath.business.exportAll());
    return response.data;
  },
};
